import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Ellipsis } from "lucide-react";

const MORE_BUTTON_WIDTH = 38;

const ResponsiveNav = ({
  items,
  className,
  ariaLabel,
  maxVisible = 6,
  chip = false,
}) => {
  const navRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const itemsKey = useMemo(
    () => items.map((item) => item.key).join("|"),
    [items],
  );

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    const measure = () => {
      const availableWidth = nav.clientWidth;
      const widths = Array.from(
        nav.querySelectorAll(".navbar-overflow-measure-item"),
      ).map((item) => item.getBoundingClientRect().width);
      const styles = window.getComputedStyle(nav);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const maxCount = Math.min(items.length, maxVisible);

      let nextCount = 0;
      for (let count = maxCount; count >= 0; count -= 1) {
        const hasOverflow = count < items.length;
        const gaps =
          Math.max(0, count - 1) + (hasOverflow && count > 0 ? 1 : 0);
        const requiredWidth =
          widths.slice(0, count).reduce((total, width) => total + width, 0) +
          gaps * gap +
          (hasOverflow ? MORE_BUTTON_WIDTH : 0);
        if (requiredWidth <= availableWidth) {
          nextCount = count;
          break;
        }
      }

      setVisibleCount((current) =>
        current === nextCount ? current : nextCount,
      );
      if (nextCount >= items.length) setMoreOpen(false);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items.length, itemsKey, maxVisible]);

  const renderItem = (item, inMoreMenu = false) => {
    const ItemIcon = item.icon;
    const itemClassName = inMoreMenu
      ? `navbar-overflow-menu-item${item.active ? " is-active" : ""}`
      : `${chip ? "hr-chip " : ""}navbar-context-link${chip ? " navbar-context-link--sub" : ""}${item.active ? " is-active" : ""}`;
    const itemContents = (
      <>
        {ItemIcon && (
          <ItemIcon className="navbar-context-icon" aria-hidden="true" />
        )}
        <span>{item.label}</span>
      </>
    );
    const handleClick = () => {
      setMoreOpen(false);
      item.onSelect?.();
    };

    if (item.to) {
      return (
        <Link
          key={item.key}
          to={item.to}
          className={itemClassName}
          role={inMoreMenu ? "menuitem" : undefined}
          aria-current={item.active ? "page" : undefined}
          onClick={handleClick}
        >
          {itemContents}
        </Link>
      );
    }

    return (
      <button
        key={item.key}
        type="button"
        className={itemClassName}
        role={inMoreMenu ? "menuitem" : undefined}
        aria-current={item.active ? "page" : undefined}
        onClick={handleClick}
      >
        {itemContents}
      </button>
    );
  };

  const overflowItems = items.slice(visibleCount);
  const hasActiveOverflowItem = overflowItems.some((item) => item.active);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!navRef.current?.contains(event.target)) setMoreOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreOpen]);

  return (
    <nav ref={navRef} className={className} aria-label={ariaLabel}>
      {items.slice(0, visibleCount).map((item) => renderItem(item))}
      {overflowItems.length > 0 && (
        <div
          className="navbar-overflow-host"
          onMouseEnter={() => setMoreOpen(true)}
          onMouseLeave={() => setMoreOpen(false)}
        >
          <button
            type="button"
            className={`navbar-more-button${hasActiveOverflowItem ? " has-active-item" : ""}`}
            aria-label="More navigation items"
            aria-haspopup="true"
            aria-expanded={moreOpen}
            onFocus={() => setMoreOpen(true)}
            onClick={() => setMoreOpen(true)}
          >
            <Ellipsis size={21} aria-hidden="true" />
          </button>
          {moreOpen && (
            <div className="navbar-overflow-menu" role="menu">
              {overflowItems.map((item) => renderItem(item, true))}
            </div>
          )}
        </div>
      )}
      <div className="navbar-overflow-measure" aria-hidden="true">
        {items.map((item) => (
          <span className="navbar-overflow-measure-item" key={item.key}>
            {renderItem(item)}
          </span>
        ))}
      </div>
    </nav>
  );
};

export default ResponsiveNav;
